/* =====================================================================
 * GuideScrollTop — 본문 오른쪽 아래의 "맨 위로" 버튼.
 *
 * 문서를 어느 정도 내려 읽은 상태에서 스크롤하는 동안에만 나타나고, 스크롤을 멈추면
 * 잠시 뒤 흐려져 사라진다. 읽는 동안 글자를 가리지 않으면서, 위로 가고 싶어 스크롤을
 * 시작하는 순간 바로 손이 닿는다. 버튼에 마우스를 올리거나 키보드로 초점을 두면
 * 사라지지 않는다.
 * ===================================================================== */

import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';

import * as S from './GuideArticle.styles';

/** 이만큼 내려가야 나타난다. 첫 화면(제목·목차 상자)에서는 필요 없다. */
export const SCROLL_TOP_THRESHOLD = 480;
/** 스크롤을 멈춘 뒤 사라지기까지의 시간. */
export const SCROLL_TOP_IDLE_MS = 1500;

/** 기준선 아래에서 최근에 스크롤했는지. 멈추고 `idleMs` 가 지나면 false. */
export function useRecentScroll(
  scrollRootRef: React.RefObject<HTMLElement>,
  threshold = SCROLL_TOP_THRESHOLD,
  idleMs = SCROLL_TOP_IDLE_MS,
): boolean {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root) return undefined;
    let timer = 0;
    const onScroll = () => {
      window.clearTimeout(timer);
      if (root.scrollTop <= threshold) {
        setActive(false);
        return;
      }
      setActive(true);
      timer = window.setTimeout(() => setActive(false), idleMs);
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      root.removeEventListener('scroll', onScroll);
      window.clearTimeout(timer);
    };
  }, [scrollRootRef, threshold, idleMs]);

  return active;
}

export function scrollGuideToTop(root: HTMLElement | null): void {
  root?.scrollTo?.({ top: 0, behavior: 'smooth' });
}

const LABEL = '맨 위로';

export const GuideScrollTop: React.FC<{ readonly scrollRootRef: React.RefObject<HTMLElement> }> = ({
  scrollRootRef,
}) => {
  const scrolling = useRecentScroll(scrollRootRef);
  const [held, setHeld] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const visible = scrolling || held;

  return (
    <S.FloatingTopSlot>
      <S.FloatingTopButton
        ref={buttonRef}
        type="button"
        $visible={visible}
        tabIndex={visible ? 0 : -1}
        aria-hidden={!visible}
        aria-label={LABEL}
        title={LABEL}
        onMouseEnter={() => setHeld(true)}
        onMouseLeave={() => setHeld(false)}
        onFocus={() => setHeld(true)}
        onBlur={() => setHeld(false)}
        onClick={() => {
          setHeld(false);
          buttonRef.current?.blur();
          scrollGuideToTop(scrollRootRef.current);
        }}
      >
        <ArrowUp size={17} aria-hidden />
      </S.FloatingTopButton>
    </S.FloatingTopSlot>
  );
};
